import emissiveTonemappingFrag from "@/components/RenderedDie/shaders/emissive_tonemapping.frag";
import {
  Color,
  FrontSide,
  MeshPhysicalMaterial,
  Texture,
  type WebGLProgramParametersWithUniforms,
} from "three";

/**
 * This is shader code exported from the Everlasting Rime
 * procedural texture shaders repurposed to run with MeshPhysicalMaterial.
 *
 * The uniforms were pulled from its manifest.
 */
export const getRimeMaterial = (
  numberTexture: string,
  maskTexture: string,
  textures: {
    [x: string]: Texture;
  },
  onBeforeCompile?: (shader: WebGLProgramParametersWithUniforms) => void,
) => {
  const material = new MeshPhysicalMaterial({
    color: new Color("#000000"),
    clearcoat: 0.75,
    clearcoatNormalMap: textures["rockNormal"],
    clearcoatRoughness: 0.0,
    side: FrontSide,
    userData: {
      // Rime is physical due to clearcoat, but we don't want the other
      // Die shader adjustments applied on top of this
      isCustomMaterial: true,
    },
  });

  material.onBeforeCompile = (shader) => {
    shader.uniforms = {
      ...shader.uniforms,
      iGlobalTime: { value: 0 },
      lastEvent: { value: 0 },
      lastEventTime: { value: 0 },
      dieValue: { value: 0 },
      numTex: { value: textures[numberTexture] },
      maskTex: { value: textures[maskTexture] },
      frostColor: { value: new Color("#404040") },
      iceColor: { value: new Color("#0a2252") },
    };

    shader.fragmentShader =
      `
        uniform float iGlobalTime;
        uniform int lastEvent;
        uniform float lastEventTime;
        uniform int dieValue;
        uniform sampler2D numTex;
        uniform sampler2D maskTex;
        uniform vec3 frostColor;
        uniform vec3 iceColor;

        #define MASK_THRESHOLD 0.03
        #define NUMBER_PULSE_SPEED 1.5
        #define NUMBER_PULSE_MAGNITUDE 0.05
        #define NUMBER_CRIT_MAGNITUDE 2.5
        #define NUMBER_CRIT_SPEED 25.0
        #define FROST_SPEED 3.0
        #define GLOW_SPEED 3.0

        #define MELT_SPEED 0.75

        // Use og shader code as a function to determine emissive color
        // which shows the die value or enforces the frosty look
        vec3 getRimeEmissive(vec2 uv) {
          vec4 numColor = texture2D(numTex, uv);
          vec4 mask = texture2D(maskTex, uv);

          int faceIndex = dieValue - 1;
          float val = -1.0;
          if (faceIndex >= 0) {
            // Approximate gray mask values using this linear function
            val = abs(mask.r - (0.05 * float(faceIndex)));
          }

          if (dieValue > 0 && val <= MASK_THRESHOLD) {
            if (lastEvent == 2) {
              // Fade in when starting to show
              float intensity = smoothstep(0.1, 0.6, GLOW_SPEED * (iGlobalTime - lastEventTime));
              numColor *= intensity;
            }

            // Amplify for crit effect
            if (lastEvent == 4 && dieValue == 20) {
              numColor *= clamp(NUMBER_CRIT_SPEED * (iGlobalTime - lastEventTime), 1.0, NUMBER_CRIT_MAGNITUDE);
            }

            // Roll complete and dieValue matches masked area
            else if (lastEvent >= 2) {
              // Thawed number portion
              numColor += NUMBER_PULSE_MAGNITUDE * sin(iGlobalTime * NUMBER_PULSE_SPEED);
            }
          } else {
            // Iced over section
            // The mixing here gives the look as if we can see the numbers map
            // even though we're just showing the emissive version of the numbers
            numColor = mix(numColor, vec4(iceColor, 0.0), 0.98);
          }

          // frosty
          if (lastEvent >= 5) {
            float deltaTime = clamp(FROST_SPEED * (iGlobalTime - lastEventTime), 0.0, 1.0);
            numColor = mix(vec4(iceColor, 0.0), vec4(frostColor, 1.0), deltaTime);
          }

          return numColor.rgb;
        }

        // Use og shader code as a function to modulate clearcoat
        // between 1.0 and 0.0 based on melt progress
        float getRimeClearcoatFactor(vec2 uv) {
          vec4 mask = texture2D(maskTex, uv);

          int faceIndex = dieValue - 1;
          float val = abs(mask.r - (0.05 * float(faceIndex)));

          float factor = 1.0;

          if (mask.r > 0.018 && val < MASK_THRESHOLD && dieValue > 0 && lastEvent >= 2) {
            if (lastEvent == 2) {
              float intensity = smoothstep(0.1, 0.6, MELT_SPEED * (iGlobalTime - lastEventTime));
              factor = 1.0 - intensity;
            } else {
              factor = 0.0;
            }
          }

          if (lastEvent >= 5) {
            factor = 1.0;
          }

          return factor;
        }
      ` + shader.fragmentShader;

    // this lights up the die value using the helper above
    shader.fragmentShader = shader.fragmentShader.replace(
      "vec3 totalEmissiveRadiance = emissive;",
      `
        vec3 rimeEmissive = vec3(0.0);
        #ifdef USE_UV
          vec2 rimeUv = vUv;
          rimeEmissive = getRimeEmissive(rimeUv);
        #endif
        vec3 totalEmissiveRadiance = emissive + rimeEmissive;
      `,
    );

    // this "melts" the clearcoat using the helper above
    shader.fragmentShader = shader.fragmentShader.replace(
      "outgoingLight = outgoingLight * ( 1.0 - material.clearcoat * Fcc ) + ( clearcoatSpecularDirect + clearcoatSpecularIndirect ) * material.clearcoat;",
      `
        float rimeClearcoatFactor = 1.0;
        #ifdef USE_UV
          rimeClearcoatFactor = getRimeClearcoatFactor( vUv );
        #endif

        float rimeClearcoat = material.clearcoat * rimeClearcoatFactor;

        outgoingLight = outgoingLight * ( 1.0 - rimeClearcoat * Fcc ) + ( clearcoatSpecularDirect + clearcoatSpecularIndirect ) * rimeClearcoat;
      `,
    );

    // add the same shader chunk used in our main dice material to reduce emissive blowout
    // on the clearcoat highlights
    const tonemapping = "#include <tonemapping_fragment>";
    shader.fragmentShader = shader.fragmentShader.replace(
      tonemapping,
      `
        ${emissiveTonemappingFrag}
        ${tonemapping}
      `,
    );

    onBeforeCompile?.(shader);
  };

  return material;
};
